import React, { useMemo } from 'react';
import { Toast } from './Toast';

/**
 * ToastContainer — Renders active toasts, grouped by screen position.
 *
 * Supports 12 positions (top/bottom/left/right × start/center/end).
 * Each position gets its own wrapper div so toasts stack correctly
 * within their region. Default position is 'bottom-end' (bottom-right).
 *
 * Not used directly — rendered inside ToastProvider.
 */
export const ToastContainer = ({ toasts, removeToast }) => {
    // Group toasts by their `position` prop so each screen corner gets its own stack
    const toastsByPosition = useMemo(() => {
        const grouped = {
            'top-start': [],
            'top-center': [],
            'top-end': [],
            'bottom-start': [],
            'bottom-center': [],
            'bottom-end': [],
            'left-start': [],
            'left-center': [],
            'left-end': [],
            'right-start': [],
            'right-center': [],
            'right-end': [],
        };

        toasts.forEach(toast => {
            const pos = toast.position || 'bottom-end';
            if (grouped[pos]) {
                grouped[pos].push(toast);
            } else {
                // Invalid position — fall back to bottom-end
                grouped['bottom-end'].push(toast);
            }
        });

        return grouped;
    }, [toasts]);

    // Early return: don't render empty container markup
    if (toasts.length === 0) return null;

    // Render one container div per active position
    return Object.entries(toastsByPosition).map(([position, positionToasts]) => {
        if (positionToasts.length === 0) return null;

        return (
            <div key={position} className={`sc-toast-container ${position}`}>
                {positionToasts.map(toast => (
                    <Toast
                        key={toast.id}
                        {...toast}
                        onClose={removeToast}
                    />
                ))}
            </div>
        );
    });
};
